import { apiFetchJson, apiGet } from './apiClient';

export type AdminIndustry = {
  id: number;
  name: string;
  description: string | null;
  is_visible?: boolean;
  updated_at: string;
  sectors_count: number;
  companies_count: number;
  records_count: number;
};

export type AdminSector = {
  id: number;
  name: string;
  description: string | null;
  industry_id: number;
  industry_name: string;
  is_visible?: boolean;
  updated_at: string;
  companies_count: number;
  records_count: number;
};

export type AdminCompany = {
  id: number;
  name: string;
  industry_id: number;
  industry_name: string;
  sector_id: number;
  sector_name: string;
  is_visible?: boolean;
  records_count: number;
  last_record_at: string | null;
  updated_at: string;
  aum?: number | null;
  employee_amount?: number | null;
};

export type CompensationRecord = {
  id: number;
  company_id: number;
  company_name?: string;
  industry_id?: number;
  industry_name?: string;
  sector_id?: number;
  sector_name?: string;
  company_role_name: string;
  role_id: number;
  role_name?: string;
  grade_level?: number;
  source: string;
  added_date: string;
  reliability_score: number;
  is_approved: boolean;
  is_role_validated: boolean;
  first_name: string | null;
  last_name: string | null;
  middle_name: string | null;
  phone_number: string | null;
  email: string | null;
  social_media: string | null;
  comment: string | null;
  created_at: string;
  updated_at: string;
  /** Из compensation_record_base */
  fix?: number | null;
  /** Доля годового бонуса (0.15 = 15%), из compensation_record_sti */
  sti?: number | null;
  /** «да» / «нет», из compensation_record_ltip */
  ltip?: string | null;
  ltip_has?: boolean;
};

export type RecordBasePart = {
  baseGrossSalary?: number | null;
  salaryReviewFrequency?: string | null;
  salaryReviewDate?: string | null;
  salaryReviewDate2?: string | null;
  salaryReviewCriteria?: string | null;
};

export type RecordStiPart = {
  stiTargetPct?: number | null;
  stiPayoutFrequency?: string | null;
  stiPayoutMonth?: string | null;
  stiPayoutMonth2?: string | null;
  stiSizingCriteria?: string | null;
  stiCorporateKpiExamples?: string | null;
  stiIndividualKpiExamples?: string | null;
  stiFormula?: string | null;
  stiCorporateKpiWeight?: number | null;
  stiIndividualKpiWeight?: number | null;
};

export type RecordLtipPart = {
  ltipForm?: string | null;
  ltipAmount?: number | null;
  targetLti?: number | null;
  ltipProgramDuration?: string | null;
  ltipVestingSchedule?: string | null;
};

export type RecordCompanyDataPart = {
  geography?: string | null;
  investmentStagePriority?: string | null;
  aumData?: string | null;
  lifetimeCycle?: string | null;
};

export type RecordParts = {
  base: RecordBasePart | null;
  sti: RecordStiPart | null;
  ltip: RecordLtipPart | null;
  companyData: RecordCompanyDataPart | null;
};

export type AdminStats = {
  industries_count: number;
  sectors_count: number;
  companies_count: number;
  records_count: number;
  recentCompanies: AdminCompany[];
  recentRecords: CompensationRecord[];
};

export type RecordFormPayload = {
  companyId: number;
  companyRoleName: string;
  source?: string;
  addedDate?: string;
  reliabilityScore?: number;
  isApproved?: boolean;
  isRoleValidated?: boolean;
  firstName?: string | null;
  lastName?: string | null;
  middleName?: string | null;
  phoneNumber?: string | null;
  email?: string | null;
  socialMedia?: string | null;
  comment?: string | null;
  roleId?: number;
  base?: RecordBasePart | null;
  sti?: RecordStiPart | null;
  ltip?: RecordLtipPart | null;
  companyData?: RecordCompanyDataPart | null;
};

export type ExcelImportRowPayload = {
  excelRowNumber: number;
  companyName: string;
  sectorName: string;
  companyRoleName: string;
  roleName?: string | null;
  datacircleGrade?: string | null;
  familyName?: string | null;
  baseGrossSalary?: number | null;
  stiTargetPct?: number | null;
  ltipForm?: string | null;
};

export type ExcelImportResult = {
  savedCount: number;
  total: number;
  saved: { excelRowNumber: number | null; recordId: number }[];
  savedTruncated?: boolean;
  errors: { excelRowNumber: number | null; error: string }[];
  errorsTruncated?: boolean;
};

export type RecordAssessmentScoringAnswer = {
  question_id: number;
  answer_id: number;
  answer_score: number;
  question_text: string;
  answer_text: string;
  factor_name: string | null;
};

export type RecordAssessmentCalibrationAnswer = {
  calibration_question_id: number;
  calibration_answer_id: number;
  question_text: string;
  answer_text: string;
  target_role_cluster_id: number | null;
};

export type RecordAssessment = {
  id: number;
  compensation_record_id: number;
  family_id: number | null;
  family_name: string | null;
  role_cluster_id: number | null;
  role_cluster_name: string | null;
  specialization_id: number | null;
  specialization_name: string | null;
  total_score: number | null;
  computed_role_id: number | null;
  computed_role_name: string | null;
  computed_grade_level: number | null;
  calibration_answer_id: number | null;
  status: string;
  algorithm_version: string | null;
  created_at: string;
  updated_at: string;
};

export type RecordAssessmentPayload = {
  mode: 'quick' | 'full';
  familyId: number;
  roleClusterId: number;
  specializationId?: number | null;
  computedRoleId: number;
  totalScore?: number | null;
  calibrationAnswerId?: number | null;
  scoringAnswers?: { questionId: number; answerId: number; answerScore: number }[];
};

export type RecordAssessmentResponse = {
  assessment: RecordAssessment | null;
  scoringAnswers: RecordAssessmentScoringAnswer[];
  calibrationAnswers: RecordAssessmentCalibrationAnswer[];
};

export async function fetchAdminStats(): Promise<AdminStats | null> {
  return apiGet<AdminStats>('/admin/stats');
}

export async function fetchIndustries(): Promise<AdminIndustry[]> {
  const res = await apiGet<{ data: AdminIndustry[] }>('/admin/industries');
  return res?.data || [];
}

export async function fetchSectors(industryId?: number): Promise<AdminSector[]> {
  const q = industryId ? `?industryId=${industryId}` : '';
  const res = await apiGet<{ data: AdminSector[] }>(`/admin/sectors${q}`);
  return res?.data || [];
}

export async function fetchCompanies(params?: {
  industryId?: number;
  sectorId?: number;
  search?: string;
}): Promise<AdminCompany[]> {
  const sp = new URLSearchParams();
  if (params?.industryId) sp.set('industryId', String(params.industryId));
  if (params?.sectorId) sp.set('sectorId', String(params.sectorId));
  if (params?.search) sp.set('search', params.search);
  const q = sp.toString() ? `?${sp}` : '';
  const res = await apiGet<{ data: AdminCompany[] }>(`/admin/companies${q}`);
  return res?.data || [];
}

export async function fetchCompanyDetail(companyId: number, sectorId?: number) {
  const q = sectorId ? `?sectorId=${sectorId}` : '';
  return apiGet<{
    company: AdminCompany;
    context: { industry_id: number; industry_name: string; sector_id: number; sector_name: string } | null;
    stats: { records_count: number; roles_count: number; last_record_at: string | null };
  }>(`/admin/companies/${companyId}${q}`);
}

export async function fetchRecords(params?: {
  companyId?: number;
  industryId?: number;
  sectorId?: number;
  search?: string;
  source?: string;
  sort?: string;
  page?: number;
  limit?: number;
  /** Грейд подтверждён = нет */
  roleNotValidated?: boolean;
  /** Запись валидирована = нет */
  notApproved?: boolean;
}) {
  const sp = new URLSearchParams();
  if (params?.companyId) sp.set('companyId', String(params.companyId));
  if (params?.industryId) sp.set('industryId', String(params.industryId));
  if (params?.sectorId) sp.set('sectorId', String(params.sectorId));
  if (params?.search) sp.set('search', params.search);
  if (params?.source) sp.set('source', params.source);
  if (params?.sort) sp.set('sort', params.sort);
  if (params?.page) sp.set('page', String(params.page));
  if (params?.limit) sp.set('limit', String(params.limit));
  if (params?.roleNotValidated) sp.set('roleNotValidated', 'true');
  if (params?.notApproved) sp.set('notApproved', 'true');
  const q = sp.toString() ? `?${sp}` : '';
  return apiGet<{ data: CompensationRecord[]; total: number; page: number; limit: number }>(
    `/admin/compensation-records${q}`,
  );
}

export async function fetchRecordSources(): Promise<string[]> {
  const res = await apiGet<{ data?: string[] }>('/admin/compensation-records/sources');
  if (!res || !Array.isArray(res.data)) return [];
  return res.data;
}

export async function updateIndustry(id: number, payload: { name?: string; description?: string; is_visible?: boolean }) {
  const res = await apiFetchJson('PATCH', `/admin/industries/${id}`, { body: payload });
  if (!res.ok) throw new Error(res.error);
  return res.data;
}

export async function deleteIndustry(id: number) {
  const res = await apiFetchJson('DELETE', `/admin/industries/${id}`);
  if (!res.ok) throw new Error(res.error);
}

export async function updateSector(id: number, payload: { name?: string; description?: string; is_visible?: boolean }) {
  const res = await apiFetchJson('PATCH', `/admin/sectors/${id}`, { body: payload });
  if (!res.ok) throw new Error(res.error);
  return res.data;
}

export async function deleteSector(id: number) {
  const res = await apiFetchJson('DELETE', `/admin/sectors/${id}`);
  if (!res.ok) throw new Error(res.error);
}

export async function updateCompany(
  id: number,
  payload: { name?: string; aum?: number | null; employeeAmount?: number | null; is_visible?: boolean },
) {
  const res = await apiFetchJson('PATCH', `/admin/companies/${id}`, { body: payload });
  if (!res.ok) throw new Error(res.error);
  return res.data;
}

export async function deleteCompany(id: number) {
  const res = await apiFetchJson('DELETE', `/admin/companies/${id}`);
  if (!res.ok) throw new Error(res.error);
}

export async function createIndustry(name: string, description?: string): Promise<AdminIndustry> {
  const res = await apiFetchJson('POST', '/admin/industries', { body: { name, description } });
  if (!res.ok) throw new Error(res.error);
  return res.data as AdminIndustry;
}

export async function createSector(industryId: number, name: string, description?: string): Promise<AdminSector> {
  const res = await apiFetchJson('POST', '/admin/sectors', { body: { industryId, name, description } });
  if (!res.ok) throw new Error(res.error);
  return res.data as AdminSector;
}

export async function createCompany(industryId: number, sectorId: number, name: string) {
  const res = await apiFetchJson('POST', '/admin/companies', { body: { industryId, sectorId, name } });
  if (!res.ok) throw new Error(res.error);
  return res.data as { id: number; name: string };
}

export async function createRecord(payload: RecordFormPayload) {
  const res = await apiFetchJson('POST', '/admin/compensation-records', { body: payload });
  if (!res.ok) throw new Error(res.error);
  return res.data as CompensationRecord;
}

export async function createRecordsBulk(companyId: number, records: Partial<RecordFormPayload>[]) {
  const res = await apiFetchJson('POST', '/admin/compensation-records/bulk', {
    body: { companyId, records },
  });
  if (!res.ok) throw new Error(res.error);
  return res.data as { savedCount: number; errors: { row: number; error: string }[] };
}

export type ExcelImportRequest = {
  industryId: number;
  source: string;
  addedDate?: string;
  rows: ExcelImportRowPayload[];
};

export async function importRecordsFromExcel(payload: ExcelImportRequest) {
  const res = await apiFetchJson('POST', '/admin/compensation-records/import-excel', { body: payload });
  if (!res.ok) throw new Error(res.error);
  return res.data as ExcelImportResult;
}

export async function updateRecord(id: number, payload: Partial<RecordFormPayload>) {
  const res = await apiFetchJson('PATCH', `/admin/compensation-records/${id}`, { body: payload });
  if (!res.ok) throw new Error(res.error);
  return res.data as CompensationRecord;
}

export async function fetchRecordParts(recordId: number): Promise<RecordParts> {
  const res = await apiFetchJson<RecordParts>('GET', `/admin/compensation-records/${recordId}/parts`);
  if (!res.ok) throw new Error(res.error);
  return res.data as RecordParts;
}

export async function saveRecordParts(recordId: number, parts: Partial<RecordParts>): Promise<RecordParts> {
  const res = await apiFetchJson<RecordParts>('PUT', `/admin/compensation-records/${recordId}/parts`, { body: parts });
  if (!res.ok) throw new Error(res.error);
  return res.data as RecordParts;
}

export async function fetchRecordAssessment(recordId: number): Promise<RecordAssessmentResponse> {
  const res = await apiFetchJson<RecordAssessmentResponse>(
    'GET',
    `/admin/compensation-records/${recordId}/assessment`,
  );
  if (!res.ok) throw new Error(res.error);
  return res.data as RecordAssessmentResponse;
}

export async function saveRecordAssessment(recordId: number, payload: RecordAssessmentPayload) {
  const res = await apiFetchJson('PUT', `/admin/compensation-records/${recordId}/assessment`, { body: payload });
  if (!res.ok) throw new Error(res.error);
  return res.data as { ok: boolean; assessmentId: number };
}

export type RecalculateAssessmentsResult = {
  ok: boolean;
  dryRun: boolean;
  totalAssessments: number;
  processed: number;
  updated: number;
  unchanged: number;
  roleChanged: number;
  noRoleMatch: number;
  skippedMissingAnswers: number;
  changes: Array<{
    recordId: number;
    oldTotalScore: number | null;
    newTotalScore: number;
    oldRoleId: number | null;
    newRoleId: number | null;
    oldRecordRoleId: number | null;
    newRecordRoleId: number | null;
    newRoleName: string | null;
    noRoleMatch: boolean;
  }>;
};

export async function recalculateRecordGradings(options?: { dryRun?: boolean }) {
  const res = await apiFetchJson<RecalculateAssessmentsResult>(
    'POST',
    '/admin/grading/recalculate-assessments',
    { body: options ?? {} },
  );
  if (!res.ok) throw new Error(res.error);
  return res.data as RecalculateAssessmentsResult;
}

export async function deleteRecord(id: number) {
  const res = await apiFetchJson('DELETE', `/admin/compensation-records/${id}`, { withAuth: true });
  if (!res.ok) throw new Error(res.error);
}

export function formatMoney(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(value);
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('ru-RU');
}

export function formatPercentFraction(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `${(value * 100).toFixed(1)}%`;
}

/** STI в БД — доля годового бонуса (0.15 = 15%), не сумма в рублях. */
export function formatSti(value: number | null | undefined): string {
  return formatPercentFraction(value);
}

export function formatYesNo(value: boolean | null | undefined): string {
  return value ? 'Да' : 'Нет';
}
